"use client";

import Image from "next/image";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Check,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCurrentUser } from "@/hooks/use-current-user";
import type { CurrentUser } from "@/lib/feed/types";
import { completeFirstUseTour } from "@/lib/profile/api";
import { queryKeys } from "@/lib/query-keys";
import { getErrorMessage } from "@/lib/utils";

type TourStep = {
  eyebrow: string;
  title: string;
  description: string;
  panelClassName: string;
  highlights: string[];
};

const TOUR_STEPS: TourStep[] = [
  {
    eyebrow: "Üdvözöljük",
    title: "Ismerje meg a PraxisTársat",
    description:
      "Néhány rövid lépésben megmutatjuk, hogyan kapcsolódhat kollégáihoz és milyen lehetőségeket érhet el.",
    panelClassName: "from-blue-50 via-indigo-50 to-white",
    highlights: ["Szakmai közösség", "Egy helyen", "Egyszerű kapcsolódás"],
  },
  {
    eyebrow: "Praxisok",
    title: "Hozza létre praxisát, vagy csatlakozzon",
    description:
      "Új praxist hozhat létre, illetve név vagy meghívókód alapján csatlakozhat egy már működő praxishoz.",
    panelClassName: "from-indigo-50 via-violet-50 to-white",
    highlights: ["Új praxis", "Csatlakozás", "Közös munkatér"],
  },
  {
    eyebrow: "Szolgáltatások",
    title: "Szakértői segítség a működéshez",
    description:
      "Válogasson a praxis működését támogató jogi, informatikai, könyvelési és további szolgáltatások közül.",
    panelClassName: "from-emerald-50 via-teal-50 to-white",
    highlights: ["Áttekinthető ajánlatok", "Szakértő partnerek", "Gyors igénylés"],
  },
  {
    eyebrow: "Chat",
    title: "Beszéljen közvetlenül kollégáival",
    description:
      "Indítson személyes vagy csoportos beszélgetést, osszon meg fájlokat, és tartsa egy helyen a szakmai egyeztetéseket.",
    panelClassName: "from-sky-50 via-cyan-50 to-white",
    highlights: ["Személyes chat", "Csoportos beszélgetés", "Fájlmegosztás"],
  },
  {
    eyebrow: "Közösségek",
    title: "Építsen saját szakmai közösséget",
    description:
      "Hozzon létre tematikus közösséget, hívjon meg tagokat, és osszon meg velük célzott bejegyzéseket.",
    panelClassName: "from-violet-50 via-fuchsia-50 to-white",
    highlights: ["Saját közösség", "Tagok meghívása", "Közösségi hírfolyam"],
  },
  {
    eyebrow: "Események",
    title: "Szervezzen szakmai eseményeket",
    description:
      "Hirdessen online vagy személyes eseményt, kezelje a jelentkezőket, és érje el a megfelelő közösséget.",
    panelClassName: "from-orange-50 via-amber-50 to-white",
    highlights: ["Online vagy személyes", "Jelentkezések", "Közösségi meghívás"],
  },
];

export function FirstUseTour() {
  const currentUserQuery = useCurrentUser();
  const queryClient = useQueryClient();
  const [stepIndex, setStepIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const user = currentUserQuery.data;
  const shouldOpen = Boolean(
    user?.accountStatus === "active" &&
      !user.onboardingCompleted &&
      !dismissed,
  );
  const step = TOUR_STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === TOUR_STEPS.length - 1;

  const completeMutation = useMutation({
    mutationFn: completeFirstUseTour,
    onSuccess: () => {
      queryClient.setQueryData<CurrentUser | null>(
        queryKeys.currentUser,
        (current) =>
          current ? { ...current, onboardingCompleted: true } : current,
      );
      setDismissed(true);
    },
  });

  function complete() {
    if (!completeMutation.isPending) {
      completeMutation.mutate();
    }
  }

  return (
    <Dialog
      open={shouldOpen}
      onOpenChange={(open) => {
        if (!open) {
          complete();
        }
      }}
    >
      <DialogContent className="max-h-[92vh] max-w-xl overflow-hidden rounded-3xl border-0 bg-white p-0 shadow-2xl">
        <div
          className={`relative overflow-hidden bg-gradient-to-br ${step.panelClassName} px-6 pt-7 pb-6 sm:px-8`}
        >
          <div className="absolute -top-16 -right-16 size-48 rounded-full bg-white/55 blur-2xl" />
          <div className="relative">
            <div className="mb-8 flex items-center justify-between gap-4 pr-8">
              <div className="flex items-center gap-2">
                {TOUR_STEPS.map((item, index) => (
                  <span
                    key={item.title}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      index === stepIndex
                        ? "w-8 bg-primary"
                        : index < stepIndex
                          ? "w-3 bg-primary/40"
                          : "w-3 bg-slate-300/80"
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs font-semibold text-slate-500">
                {stepIndex + 1}/{TOUR_STEPS.length}
              </span>
            </div>

            <div className="flex size-16 items-center justify-center rounded-2xl border border-white/80 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
              <Image
                src="/brand/praxistars-mark.png?v=2"
                alt="PraxisTárs"
                width={48}
                height={48}
                className="size-12 object-contain"
              />
            </div>

            <DialogHeader className="mt-6 pr-4">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">
                {step.eyebrow}
              </p>
              <DialogTitle className="max-w-md text-2xl leading-tight text-slate-950 sm:text-3xl">
                {step.title}
              </DialogTitle>
              <DialogDescription className="max-w-lg pt-1 text-sm leading-6 text-slate-600">
                {step.description}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 flex flex-wrap gap-2">
              {step.highlights.map((highlight) => (
                <span
                  key={highlight}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white bg-white/80 px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm"
                >
                  <Check className="size-3.5 text-primary" />
                  {highlight}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 px-6 py-5 sm:flex-row sm:items-center sm:px-8">
          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground sm:mr-auto"
            disabled={completeMutation.isPending}
            onClick={complete}
          >
            Bemutató kihagyása
          </Button>

          {!isFirst ? (
            <Button
              type="button"
              variant="outline"
              className="rounded-full"
              onClick={() => setStepIndex((current) => current - 1)}
            >
              <ArrowLeft className="size-4" />
              Előző
            </Button>
          ) : null}

          <Button
            type="button"
            className="rounded-full px-5"
            disabled={completeMutation.isPending}
            onClick={() => {
              if (isLast) {
                complete();
              } else {
                setStepIndex((current) => current + 1);
              }
            }}
          >
            {isLast ? "Kezdjük el" : "Következő"}
            {isLast ? (
              <Check className="size-4" />
            ) : (
              <ArrowRight className="size-4" />
            )}
          </Button>
        </div>

        {completeMutation.isError ? (
          <p className="px-8 pb-5 text-sm text-destructive">
            {getErrorMessage(completeMutation.error)}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
